import React, { useEffect, useState } from 'react';
import Tooltip from '@atlaskit/tooltip';
import { REQUEST_TYPE } from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';
import type { AssetOption } from '@atlassian/jira-issue-field-assets/src/common/types.tsx';
import AssetsEdit from '@atlassian/jira-issue-field-assets/src/ui/edit/main.tsx';
import { FieldContainer } from '../../common/ui/fields/styled.tsx';
import { StyledFieldSkeleton } from '../../common/ui/styled-common/styled.tsx';
import { withFormField } from '../../common/ui/with-form-field/index.tsx';
import { closeMenuOnScroll } from '../../common/utils/select/index.tsx';
import type { Props, AssetsFieldConfig, FieldValue, AssetEntityForServer } from './types.tsx';
import { transformDefaultAssets, useFetchAssetsWithId } from './utils.tsx';

const AssetsField = (props: Props) => {
	const {
		autoCompleteUrl,
		width,
		fieldProps: { value, onChange },
		// @ts-expect-error - TS2339 - Property 'onCloseMenuOnScroll' does not exist on type 'Props'.
		onCloseMenuOnScroll = closeMenuOnScroll,
		// @ts-expect-error - TS2339 - Property 'isDropdownMenuFixedAndLayered' does not exist on type 'Props'.
		isDropdownMenuFixedAndLayered = false,
		error,
		fieldId,
		defaultValue,
		isEditable = true,
		nonEditableReason,
		selectedProject,
		selectedIssueType,
		ariaLabelledBy,
	} = props;

	const [shouldFetchServiceName, setShouldFetchServiceName] = useState(true);
	const [selectedOptions, setSelectedOptions] = useState<AssetOption[]>([]);
	const [{ isLoading }, fetchAssetsName] = useFetchAssetsWithId();
	const projectId = selectedProject?.value || '';

	let issueTypeId: string | undefined;
	if (selectedIssueType) {
		issueTypeId =
			selectedIssueType.type === REQUEST_TYPE
				? selectedIssueType.issueTypeId
				: selectedIssueType.value;
	}

	/*
	 * Fetch the asset names using the IDs from the default values that were saved
	 * */
	useEffect(() => {
		if (shouldFetchServiceName && defaultValue && defaultValue.length > 0) {
			/*
			 * defaultValue will be of type AssetEntityForServer but flow gives error
			 * when trying to typecast it
			 * */
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const ids = (defaultValue as AssetEntityForServer[]).map(
				({ serializedOrigin }) => serializedOrigin,
			);
			fetchAssetsName(ids, setSelectedOptions);
			setShouldFetchServiceName(false);
		} else {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setSelectedOptions(value);
		}
	}, [defaultValue, fetchAssetsName, setSelectedOptions, shouldFetchServiceName, value]);

	const getAssetsEdit = () => (
		<AssetsEdit
			autoCompleteUrl={autoCompleteUrl}
			onChange={onChange}
			value={selectedOptions}
			fieldId={fieldId}
			isInvalid={Boolean(error)}
			isDisabled={!isEditable}
			isDropdownMenuFixedAndLayered={isDropdownMenuFixedAndLayered}
			onCloseMenuOnScroll={onCloseMenuOnScroll}
			issueTypeId={issueTypeId}
			projectId={projectId}
			fetchSuggestionsOnFocus
			ariaLabelledBy={ariaLabelledBy}
		/>
	);
	if (isLoading) {
		return <StyledFieldSkeleton />;
	}

	return (
		<FieldContainer width={width}>
			{isEditable === false ? (
				<Tooltip content={nonEditableReason?.message || ''}>{getAssetsEdit()}</Tooltip>
			) : (
				getAssetsEdit()
			)}
		</FieldContainer>
	);
};

export default withFormField({
	transformDefaultValue: transformDefaultAssets,
	// @ts-expect-error - TS2345 - Argument of type '(props: Props) => JSX.Element' is not assignable to parameter of type 'AbstractComponent<ComponentProps<AssetsFieldConfig, FieldValue>, any>'.
})<AssetsFieldConfig, FieldValue>(AssetsField);
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { mutateAssetsFields } from './utils';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { AssetEntityForServer } from './types';
